// =============================================================================
// MeuDriver — M3: Aluno — Meus Certificados
// =============================================================================

import { useState } from 'react'
import { Award, CheckCircle2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useMyCertificates } from '@/hooks/useCertificates'
import { CertificateCard } from '@/components/shared/CertificateCard'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { CertificateRow } from '@/hooks/useCertificates'

// ---------------------------------------------------------------------------
// Linha de certificado
// ---------------------------------------------------------------------------

function CertRow({
  cert,
  onView,
}: {
  cert: CertificateRow
  onView: (c: CertificateRow) => void
}) {
  const instructor = cert.instructor as {
    profile?: { full_name?: string | null } | null
  } | null
  const instructorName = instructor?.profile?.full_name ?? 'Instrutor'

  const issuedDate = format(parseISO(cert.issued_at), "dd 'de' MMMM 'de' yyyy", {
    locale: ptBR,
  })

  return (
    <div
      className="flex items-center gap-4 px-5 py-4 border-b last:border-0"
      data-testid="cert-row"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-50 ring-2 ring-teal-100">
        <Award className="h-5 w-5 text-teal-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {cert.certificate_number}
          </p>
          <Badge className="bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-100 shrink-0 gap-1 hidden sm:flex">
            <CheckCircle2 className="h-3 w-3" />
            Válido
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Com {instructorName} · {cert.total_lessons} aulas · {cert.total_hours}h
        </p>
        <p className="text-xs text-muted-foreground">Emitido em {issuedDate}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onView(cert)}
        data-testid="view-cert-btn"
        className="shrink-0"
      >
        Ver
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function StudentCertificates() {
  const { data: certs, isLoading } = useMyCertificates()
  const [previewCert, setPreviewCert] = useState<CertificateRow | null>(null)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Award className="h-6 w-6 text-teal-600" />
          Meus Certificados
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Certificados de conclusão emitidos pelos seus instrutores.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : !certs || certs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 mb-4">
                <Award className="h-7 w-7 text-gray-300" />
              </div>
              <p className="font-medium text-gray-600">Nenhum certificado ainda</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                Complete 10 ou mais aulas com um instrutor para receber seu certificado.
              </p>
            </div>
          ) : (
            <div>
              {certs.map((cert) => (
                <CertRow key={cert.id} cert={cert} onView={setPreviewCert} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview dialog */}
      <Dialog
        open={!!previewCert}
        onOpenChange={(o) => { if (!o) setPreviewCert(null) }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Certificado de Conclusão</DialogTitle>
          </DialogHeader>
          {previewCert && <CertificateCard certificate={previewCert} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}
